import { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { InvestorService } from '@/services/investorService';
import { DocumentService } from '@/services/documentService';
import { emptyUploadFields, validateUploadFields, type UploadChannel, type UploadFields } from '@/services/investorUploadBatch';
import { hiddenRolesFromAllowedRoles } from '@/utils/investor/documentVisibility';
import { useBeforeUnloadWarning } from '@/hooks/useBeforeUnloadWarning';
import { toast } from 'sonner';
import { getErrorMessage } from '@/utils/errorMessage';
import type { DocumentCategory, InvestorDocument } from '@/types/investor';
import { BatchDocumentUploadModal } from './BatchDocumentUploadModal';
import { DocumentUploadFields } from './DocumentUploadFields';

interface DocumentUploadModalProps {
  investorId: string;
  organizationId: string;
  documentType: UploadChannel;
  availableCategories: DocumentCategory[];
  isOpen: boolean;
  onClose: () => void;
  onUploadComplete: () => void;
  replaceTarget?: InvestorDocument | null;
  channels?: UploadChannel[];
  defaultChannel?: UploadFields['channel'];
  onPendingChange?: (pending: boolean) => void;
}

export function DocumentUploadModal(props: DocumentUploadModalProps) {
  if (!props.isOpen) {
    return null;
  }
  return props.replaceTarget
    ? <ReplaceDocumentUploadModal key={props.replaceTarget.id} {...props} replaceTarget={props.replaceTarget} />
    : <BatchDocumentUploadModal investorId={props.investorId} organizationId={props.organizationId}
      availableCategories={props.availableCategories} channels={props.channels ?? [props.documentType]}
      defaultChannel={props.defaultChannel ?? props.documentType} onClose={props.onClose}
      onUploadComplete={props.onUploadComplete} onPendingChange={props.onPendingChange} />;
}

function ReplaceDocumentUploadModal(props: DocumentUploadModalProps & { replaceTarget: InvestorDocument }) {
  const target = props.replaceTarget;
  const { onPendingChange } = props;
  const [fields, setFields] = useState<UploadFields>(() => ({
    ...emptyUploadFields(props.documentType), category: target.category,
    subType: target.subType || '', effectiveDate: target.effectiveDate || '',
    hiddenRoles: target.hiddenRoles ?? hiddenRolesFromAllowedRoles(target.allowedRoles),
    linkedTabs: target.linkedTabs ?? []
  }));
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const [failure, setFailure] = useState('');
  const input = useRef<HTMLInputElement>(null);
  const returnFocus = useRef(document.activeElement as HTMLElement | null);
  useBeforeUnloadWarning(busy);
  useEffect(() => {
    onPendingChange?.(busy);
    return () => onPendingChange?.(false);
  }, [busy, onPendingChange]);

  const upload = async (file: File) => {
    if (busyRef.current) {
      return;
    }
    const error = DocumentService.validateFile(file) ||
      validateUploadFields(fields, [...props.availableCategories, target.category], [props.documentType]);
    if (error) {
      toast.error(error); return;
    }
    if (!target.documentGroupId) {
      setFailure('The document lineage is missing. Refresh the document list before replacing.');
      return;
    }
    busyRef.current = true; setBusy(true); setFailure('');
    try {
      await InvestorService.uploadInvestorDocument(
        props.investorId, file, fields.category, props.documentType,
        fields.notes || undefined, props.organizationId, fields.subType.trim() || undefined,
        await DocumentService.computeFileHash(file), fields.effectiveDate || undefined,
        target.hiddenRoles ?? hiddenRolesFromAllowedRoles(target.allowedRoles), target.linkedTabs ?? [],
        target.documentGroupId, target.displayName ?? null
      );
      toast.success('Document replaced');
      props.onUploadComplete(); props.onClose();
    } catch (failed) {
      setFailure(getErrorMessage(failed, 'Failed to replace document'));
    } finally {
      busyRef.current = false; setBusy(false);
      if (input.current) {
        input.current.value = '';
      }
    }
  };
  const close = () => {
    if (!busyRef.current) {
      props.onClose();
    }
  };
  return <Dialog open onOpenChange={open => {
    if (!open) {
      close();
    }
  }}>
    <DialogContent className="max-h-[90dvh] overflow-y-auto" onInteractOutside={event => event.preventDefault()}
      onCloseAutoFocus={event => {
        event.preventDefault(); returnFocus.current?.focus();
      }}>
      <DialogTitle>Replace {target.category}</DialogTitle>
      <DialogDescription>Replacing v{target.version}. The previous version remains in document history.</DialogDescription>
      <DocumentUploadFields value={fields} onChange={setFields} categories={props.availableCategories}
        channels={[props.documentType]} disabled={busy} lockCategory lockVisibility />
      {failure && <p role="alert" className="text-sm text-destructive">{failure}</p>}
      <label className="min-w-0 space-y-2 text-sm">Select File
        <input ref={input} type="file" accept={DocumentService.FILE_INPUT_ACCEPT} disabled={busy}
          className="block w-full min-w-0 text-sm"
          onChange={event => {
            const file = event.target.files?.[0]; if (file) {
              void upload(file);
            }
          }} />
      </label>
      <p className="text-xs text-muted-foreground">Accepted formats: {DocumentService.ACCEPTED_FORMATS_LABEL} (Max {DocumentService.MAX_FILE_SIZE_MB}MB)</p>
      {busy && <p className="text-sm text-muted-foreground">
        Keep this page open until the replacement finishes. Leaving may interrupt it, and you’ll need to select the file again.
      </p>}
      <div className="flex flex-wrap justify-end gap-2">
        <Button variant="outline" disabled={busy} onClick={close}>Cancel</Button>
        <Button disabled={busy} onClick={() => input.current?.click()}>
          {busy ? 'Uploading...' : 'Upload'}
        </Button>
      </div>
    </DialogContent>
  </Dialog>;
}
